import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import tippy from 'tippy.js';
import { useEffect } from 'react';
import { buildMentionExtension } from './extensions/mention-config';
import { buildEmojiSuggestionExtension } from './extensions/emoji-suggestion-config';
import { EmojiNode } from './extensions/emoji-node';
import { ImageNode } from './extensions/image-node';
import { VideoNode } from './extensions/video-node';
import { MentionPopup } from './mention-popup';
import { EmojiPopup } from './emoji-popup';
import { RichTextBubbleMenu } from './bubble-menu';
import { RichTextActionBar } from './action-bar';
import type { RichTextEditorProps } from './types';
import type { RichTextDoc } from '@org/model';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildSuggestionRenderer(Component: React.ComponentType<any>): () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStart: (props: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate: (props: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown: (props: any) => boolean;
  onExit: () => void;
} {
  return () => {
    let component: ReactRenderer | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let popup: any = null;

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onStart: (props: any) => {
        component = new ReactRenderer(Component, { props, editor: props.editor });
        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onUpdate: (props: any) => {
        component?.updateProps(props);
        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onKeyDown: (props: any) => {
        if (props.event.key === 'Escape') { popup?.[0]?.hide(); return true; }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (component?.ref as any)?.onKeyDown?.(props) ?? false;
      },
      onExit: () => { popup?.[0]?.destroy(); component?.destroy(); },
    };
  };
}

const EMPTY_DOC: RichTextDoc = { type: 'doc', content: [{ type: 'paragraph', content: [] }] };

export function RichTextEditor({ value, onChange, onSubmit, searchUsers, uploadMedia, placeholder, hint, autoFocus, minHeight = 120, className }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Underline,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      buildMentionExtension(searchUsers, buildSuggestionRenderer(MentionPopup)),
      buildEmojiSuggestionExtension(buildSuggestionRenderer(EmojiPopup)),
      EmojiNode,
      ImageNode,
      VideoNode,
    ],
    content: value ?? EMPTY_DOC,
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none px-3 py-2`,
        style: `min-height:${minHeight}px`,
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && onSubmit) {
          onSubmit();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getJSON() as RichTextDoc),
  });

  useEffect(() => {
    if (!editor || !value) return;
    const current = editor.getJSON();
    if (JSON.stringify(current) === JSON.stringify(value)) return;
    editor.commands.setContent(value, false);
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className={`rounded-lg border border-border bg-card ${className ?? ''}`}>
      <RichTextBubbleMenu editor={editor} />
      <EditorContent editor={editor} />
      <RichTextActionBar editor={editor} uploadMedia={uploadMedia} hint={hint} />
    </div>
  );
}
